import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus } from 'lucide-react';
import api from '@/lib/api';
import { PollQuestionForm } from '../components';
import type { PollQuestion } from '../types';

export function CreatePollPage() {
  const questionCounterRef = useRef(0);

  const nextQuestionId = useCallback((): string => {
    return `q_${++questionCounterRef.current}`;
  }, []);

  const emptyQuestion = useCallback((): PollQuestion => {
    return {
      questionId: nextQuestionId(),
      text: '',
      type: 'single_choice',
      options: ['', ''],
      required: true,
    };
  }, [nextQuestionId]);
  const navigate = useNavigate();
  const { pollId } = useParams<{ pollId: string }>();
  const isEditing = Boolean(pollId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [resultsVisibility, setResultsVisibility] = useState<'public' | 'creator_only'>('public');
  const [closesAt, setClosesAt] = useState('');
  const [questions, setQuestions] = useState<PollQuestion[]>([emptyQuestion()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingPoll, setLoadingPoll] = useState(isEditing);

  useEffect(() => {
    if (!pollId) return;

    async function loadPoll() {
      try {
        const poll = await api.getPoll(pollId!);
        setTitle(poll.title);
        setDescription(poll.description || '');
        setResultsVisibility(poll.resultsVisibility as 'public' | 'creator_only');
        setClosesAt(poll.closesAt ? poll.closesAt.slice(0, 16) : '');
        setQuestions(poll.questions as PollQuestion[]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load poll');
      } finally {
        setLoadingPoll(false);
      }
    }

    loadPoll();
  }, [pollId]);

  const handleSave = async (publish: boolean) => {
    setSaving(true);
    setError(null);

    try {
      const input = {
        title: title.trim(),
        description: description.trim() || undefined,
        questions,
        resultsVisibility,
        closesAt: closesAt ? new Date(closesAt).toISOString() : undefined,
      };

      let savedPollId: string;

      if (isEditing && pollId) {
        const updated = await api.updatePoll(pollId, input);
        savedPollId = updated.pollId;
      } else {
        const created = await api.createPoll(input);
        savedPollId = created.pollId;
      }

      if (publish) {
        await api.publishPoll(savedPollId);
      }

      navigate(`/a/arche_polls/${savedPollId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save poll');
    } finally {
      setSaving(false);
    }
  };

  if (loadingPoll) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{isEditing ? 'Edit Poll' : 'Create Poll'}</h1>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What is your poll about?"
          />
        </div>

        <div>
          <Label htmlFor="description">Description (optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Provide more context about your poll..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="visibility">Results Visibility</Label>
            <select
              id="visibility"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={resultsVisibility}
              onChange={(e) => setResultsVisibility(e.target.value as 'public' | 'creator_only')}
            >
              <option value="public">Public (visible to respondents)</option>
              <option value="creator_only">Creator Only</option>
            </select>
          </div>

          <div>
            <Label htmlFor="closesAt">Auto-close Date (optional)</Label>
            <Input
              id="closesAt"
              type="datetime-local"
              value={closesAt}
              onChange={(e) => setClosesAt(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base">Questions</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQuestions([...questions, emptyQuestion()])}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Question
            </Button>
          </div>

          {questions.map((q, i) => (
            <PollQuestionForm
              key={q.questionId}
              question={q}
              index={i}
              onChange={(updated) => {
                const newQuestions = [...questions];
                newQuestions[i] = updated;
                setQuestions(newQuestions);
              }}
              onRemove={() => {
                if (questions.length > 1) {
                  setQuestions(questions.filter((_, j) => j !== i));
                }
              }}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 pt-4 border-t">
          <Button variant="outline" onClick={() => navigate('/a/arche_polls')} disabled={saving}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save as Draft'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving}>
            {saving ? 'Publishing...' : 'Publish'}
          </Button>
        </div>
      </div>
    </div>
  );
}
